<template>
	<div>
		<core-layout-headline-with-tabs :tabs="tabs">
			{{ $t("course_detail") }}
		</core-layout-headline-with-tabs>

		<core-layout-content full>
			<!-- Language selector -->
			<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
				<div class="pr2 xs-12 fs3 lh125 fw7 pb1">{{ $t("language") }}</div>
				<core-select-locale
					class="xs-12"
					size="xsmall"
					v-model="selectedLocale"
				/>
			</div>

			<!-- Spinner -->
			<div v-if="isLoading" class="flex xs-middle xs-center p4 bg5 b1 bc7">
				<j-spinner :delay="0" />
			</div>

			<div v-if="course" class="flex wrap xs-12">
				<!-- Course Menu -->
				<div class="xs-12 md-3 pr2">
					<div class="p2 fs4 fw7">{{ computedCourseTitle }}</div>

					<!-- Warning if weight !== 1.0 -->
					<div class="bgWarning p4 mb2 r2" v-if="computedTotalWeight !== 1">
						<div class="flex xs-middle">
							<crm-icon name="warning" />
							<div class="fs3">{{ $t("total_weight_should_be_1") }}</div>
						</div>
						<div class="fs3 c6 mt2">
							{{ $t("total_weight") }}: {{ computedTotalWeight }}
						</div>
					</div>

					<j-card class="p2 bg5 fs3 c6" v-if="!sections.length">
						{{ $t("errors.no_sections") }}
					</j-card>
					<j-card v-if="sections.length" class="bg5">
						<div v-for="(section, i) in sections" :key="i">
							<course-content-menu
								:class="{ 'bb1 bc7': i + 1 < sections.length }"
								:section="section"
								:courseId="id"
								:selectedLesson="selectedLesson"
								:selectedLocale="selectedLocale"
								@lesson:select="onSelectLesson"
								@lesson:removed="onLessonRemove"
								@refetch="fetchCourse"
							/>
						</div>
					</j-card>
					<j-button class="mt6" size="small" @click="openAddSectionModal"
						>+ {{ $t("add_section") }}</j-button
					>
				</div>

				<!-- Show editor / preview of the selected lesson -->
				<div class="xs-12 md-9">
					<lesson-content
						v-if="selectedLesson"
						:lesson="computedLesson"
						:courseId="id"
						:sectionId="currentSection"
						:selectedLocale="selectedLocale"
						@lesson:updated="fetchCourse"
					/>
					<div v-else>
						<div class="p2 fs4 fw7">{{ "---" }}</div>
						<j-card class="xs-12 bg5 p4 fs3">
							{{ $t("no_lesson_selected") }}
						</j-card>
					</div>
				</div>
			</div>
		</core-layout-content>

		<!-- Create section modal -->
		<core-modal name="createSectionModal" :headline="$t('add_section')">
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<!-- 
				<div class="mb2 fw7">{{ $t("description") }}</div>
				<j-base-input-text class="mb8" v-model="form.description" />
				-->
				<j-button @click="addSectionToCourse">{{ $t("add") }}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineWithTabs from "@/modules/core/components/CoreLayoutHeadlineWithTabs"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"
import CoreModal from "@/modules/core/components/CoreModal"
import JCard from "@/components/JCard"
import JSpinner from "@/components/JSpinner"
import CrmIcon from "@/components/CrmIcon"
import JButton from "@/components/JButton"
import JBaseInputText from "@/components/JBaseInputText"
import LessonContent from "../components/LessonContent"
import CourseContentMenu from "../components/CourseContentMenu"

import api from "@/api/gql.js"
import queries from "../utils/queries.js"
import { i18n } from "@/locales/setup.js"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineWithTabs,
		CoreSelectLocale,
		CoreModal,
		CrmIcon,
		JCard,
		JSpinner,
		JButton,
		JBaseInputText,
		LessonContent,
		CourseContentMenu
	},

	props: {
		// Course Id
		id: String
	},

	computed: {
		sections() {
			return this.course?.sections || []
		},
		computedLesson() {
			return this.findLesson(this.sections, this.selectedLesson)
		},
		computedCourseTitle() {
			return this.course?.name
		},
		computedTotalWeight() {
			return parseFloat(this.getTotalWeight(this.course?.sections).toFixed(4))
		}
	},

	data() {
		return {
			isLoading: false,
			course: null,
			selectedLesson: null,
			currentSection: null,
			selectedLocale: i18n.locale,
			tabs: [
				{
					id: "overview",
					name: this.$t("overview"),
					to: { name: "CourseDetail", params: { ...this.$route.params } }
				},
				{
					id: "content",
					name: this.$t("content"),
					to: { name: "CourseContent", params: { ...this.$route.params } }
				}
			],

			form: {
				name: null,
				description: null
			},
			editLessonId: null
		}
	},

	methods: {
		// Find a lesson in a tree
		findLesson(sections, lessonId) {
			if (!lessonId) return null
			let lessonFound = null
			for (const i in sections) {
				lessonFound = sections[i].lessons?.find(l => l.id === lessonId)
				if (lessonFound) return lessonFound
				if (sections[i]?.sections)
					lessonFound = this.findLesson(sections[i].sections, lessonId)
				if (lessonFound) return lessonFound
			}
		},

		onSelectLesson(payload) {
			if (payload.lessonId === this.selectedLesson) return
			this.selectedLesson = payload.lessonId
			this.currentSection = payload.sectionId
		},

		openAddSectionModal() {
			this.$modal.show("createSectionModal")
			this.form.name = null
			this.form.description = null
		},

		onLessonRemove() {
			this.selectedLesson = null
			this.currentSection = null
			this.fetchCourse()
		},

		async fetchCourse() {
			this.isLoading = true
			const res = await api({
				locale: this.selectedLocale,
				query: queries.fetchCourse,
				variables: {
					where: { id: this.id }
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.course = res?.data?.courses?.list[0]
		},

		async addSectionToCourse() {
			this.isLoading = true
			/*
			 * Add section to course
			 */
			let variables = {
				courseId: this.id
			}

			let res = await api({ query: queries.addSectionToCourse, variables })

			if (res?.data?.addSectionToCourse?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			const createdSectionId = res.data.addSectionToCourse?.createdStatus?.id
			if (!createdSectionId) return
			/*
			 * Upsert name in L10n table
			 */
			if (this.form?.name?.length) {
				variables = {
					l10n: {
						key: `sections-${createdSectionId}-name`,
						locale: this.selectedLocale,
						value: this.form?.name
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			this.isLoading = false
			this.form.name = null
			this.$modal.hide("createSectionModal")
			this.fetchCourse()
		},

		// Add all section weights
		getTotalWeight(sections) {
			if (!sections || !sections.length) return 0
			return sections.reduce(
				(acc, section) => acc + this.getSectionWeight(section),
				0
			)
		},
		// Sections have lessons and also sub sections (recursive structure)
		// Returns the sum of all lesson weight in the section + the duration of all lessons in sub sections
		getSectionWeight(section) {
			if (!section) return 0
			const sumLessonWeight = section?.lessons?.reduce(
				(acc, lesson) => acc + lesson?.weight,
				0
			)
			const sumSectionsWeight = this.getTotalWeight(section?.sections)
			return sumLessonWeight + sumSectionsWeight
		}
	},

	created() {
		this.fetchCourse()
	},

	watch: {
		selectedLocale: {
			handler(o, n) {
				if (o && n) {
					this.fetchCourse()
				}
			}
		}
	}
}
</script>
